<template>
  <div v-if="!isEmpty(member)" class="member-profile">
    <PageTitle
      :title="$t('member.memberProfile.title')"
      icon="chevron_left"
      style="padding-bottom: 24px"
      :btn="$t('member.memberProfile.syncToPos')"
      :hideBtn1="!Object.values(showControl.btn.syncPOS).some(Boolean)"
      :btnLoading="loading.syncPOSMember"
      @iconClick="$router.push({ name: 'MembersManage' })"
      @btnClick="syncPOSMember"
    />
    <!-- ANCHOR MemberInfoBlock -->
    <MemberInfoBlock />

    <!-- 會員個人頁面 -->

    <section>
      <MemberPageTabs>
        <MemberProfileTabs
          :active-tab="activeTab"
          :member="member"
          :member-id="memberId"
          :shop-points="memberTopInfo?.shopPoints || []"
          :level-list="levelList"
          :rebate-wallet-balance="rebateWalletBalance"
          :point-wallet-balance="pointWalletBalance"
          :use-shop-rebate="useShopRebate"
          :external-wallet-name="externalWalletName"
          :notify-message-stack="notifyMessageStack"
          :on-error="onError"
          @tab-click="tabsClick"
          @update:activeTab="onUpdateActiveTab"
          @create-reservation="refreshReservationStatistic"
          @update-wallet="fetchMemberTopInfo"
          @update-point="fetchMemberTopInfo"
          @update-member="fetchMemberTopInfo"
        >
          <!-- 訊息提醒 tab 的特殊內容 -->
          <template #notify-tab>
            <FlexLayout col :gap="40" style="width: 440px">
              <FlexLayout col :gap="12">
                <MessageBlock
                  v-for="(block, index) in notifyMessageStack"
                  :key="`block-${index}`"
                  class="mb-3"
                  maxContent="500"
                  :model.sync="notifyMessageStack[index]"
                  :closeBtn="notifyMessageStack.length > 1"
                  :imgCropper="{
                    fixedRatio: false,
                  }"
                  @error="onError = true"
                  @checked="onError = false"
                  @close="removeNotifyMessage(index)"
                />

                <BaseElButton
                  v-if="notifyMessageStack.length < 5"
                  plain
                  icon="el-icon-plus"
                  style="
                  width: 440px;
                  background: transparent;
                  color: #5e5e5e;
                  border: solid 2px #dadada;
                "
                  @click="addBlock"
                >
                  {{ $t('member.common.add.text') }}
                </BaseElButton>
              </FlexLayout>

              <FlexLayout>
                <BaseElButton
                  style="width: 100%"
                  plain
                  block
                  @click="resetMessage"
                >
                  {{ $t('member.common.button.cancel.text') }}
                </BaseElButton>
                <BaseElButton
                  style="width: 100%"
                  block
                  type="primary"
                  @click="sendMessage"
                >
                  {{ $t('member.memberProfile.send') }}
                </BaseElButton>
              </FlexLayout>
            </FlexLayout>
          </template>
        </MemberProfileTabs>
      </MemberPageTabs>
    </section>
  </div>
</template>

<script>
import MemberPageTabs from '@/components/Tabs/MemberPageTabs'

import MemberInfoBlock from './components/MemberInfoBlock/MemberInfoBlock.vue'
import MessageBlock from '@/components/Message/MessageBlock.vue'
import MemberProfileTabs from './components/MemberProfileTabs'

// api
import {
  GetMemberTopInfo,
  UpdateMember,
  sentLineMessage,
} from '@/api/member'
import {
  SyncPospalMember,
  CheckPospalMemberSync,
  GetPospalClientConfig,
} from '@/api/pospal'

// Utils
import { isEmptyObj } from '@/utils/helper'
import ls from '@/utils/localstorage'
import { defineComponent, computed, ref, reactive, onMounted, provide, watch } from 'vue'
import { t } from '@/plugins/i18n/i18n'
import store from '@/store'
import { useRouter, useRoute } from 'vue-router/composables'
import { useTagStore } from '../Marketing/components/useTagStore'
import { useShop } from '@/use/shop'
import { get, isNull, map, omit, find, filter, reduce, join } from 'lodash'
import { usePermissions } from '@/use/permissions'
import { useFetch } from '@/use/fetch'
import { memberProfileTabs } from '@/config/member'
import { SyncTkhspcMemberImmidately } from '@/api/customModule'
import { SyncCyjPosMemberImmediately } from '@/api/cyjPos'

export default defineComponent({
  name: 'MemberProfile',
  components: {
    MemberPageTabs,
    MemberInfoBlock,
    MessageBlock,
    MemberProfileTabs,
  },

  // 頁面更新時重新取得資料
  async beforeRouteUpdate (to, from, next) {
    next()
  },

  setup () {
    // ========================================
    // Composables & Router
    // ========================================
    const router = useRouter()
    const route = useRoute()
    const { shopId } = useShop()
    const { checkAction, checkActionList } = usePermissions()
    const { simpleFetch } = useFetch()
    const { tags, fetchTagsOnMounted } = useTagStore()

    // ========================================
    // Refs & Reactive State
    // ========================================
    const memberId = computed(() => route.params.id)
    const memberTopInfo = ref(null)
    const activeTab = ref('info')
    const showPospalSyncBtn = ref(false)
    const onError = ref(false)

    const loading = reactive({
      fetchingTopInfo: false,
      syncPOSMember: false,
    })

    const notifyMessageStack = ref([
      {
        type: 'text',
        content: '',
        action: undefined,
        actionContent: undefined,
      },
    ])

    const reservationStatistic = reactive({
      total: [0],
      absence: [0],
    })

    // ========================================
    // Computed Properties
    // ========================================

    // Vuex getters
    const shop = computed(() => store.getters.shop)

    // Member 相關
    const member = computed(() => {
      if (!memberTopInfo.value) return {}

      const { member: memberData, memberUserInfo, wallet, mTags, systemMTag, memberReferralCode, referrer } = memberTopInfo.value

      return {
        ...memberData,
        referrer,
        memberReferralCode,
        UserInfo: memberUserInfo,
        Wallet: wallet,
        MTags: mTags || [],
        SystemMTag: systemMTag || [],
      }
    })

    // Level 相關
    const levelList = computed(() => {
      return get(store.state, 'shop.shopSetting.memberLevelConfig', [])
    })

    // Wallet & Balance 相關
    const rebateWalletBalance = computed(() => {
      return memberTopInfo.value?.cashback?.balance ?? 0
    })

    const totalPaymentPriceSum = computed(() => {
      return memberTopInfo.value?.salesRecordRecognizedAmountSum?.salesRecordRecognizedAmountSum ?? 0
    })

    const pointWalletBalance = computed(() => {
      if (!memberTopInfo.value?.shopPointWallets) return {}

      const shopPoints = memberTopInfo.value.shopPoints || []
      const shopPointWallets = memberTopInfo.value.shopPointWallets || []

      const findMatchedShopPoint = (walletShopPointKey) => {
        return (
          find(shopPoints, (point) => point.key === walletShopPointKey) ||
          find(shopPoints, (point) => `${point.sourceShopId}_${point.key}` === walletShopPointKey)
        )
      }

      return reduce(shopPointWallets, (acc, wallet) => {
        const point = findMatchedShopPoint(wallet.shopPointKey)
        if (point && !wallet.error) {
          acc[wallet.shopPointKey] = {
            balance: wallet.balance ?? 0,
            pointName: point.name,
            pointUnitName: point.unitName || '',
            sourceShopIsHeadquarter: point.sourceShopIsHeadquarter ?? false,
          }
        }
        return acc
      }, {})
    })

    const pospalWalletBalance = computed(() => {
      return memberTopInfo.value?.externalWallet?.balance ?? 0
    })

    const externalPointWalletBalance = computed(() => {
      const balance = memberTopInfo.value?.externalPoint?.balance
      return balance ?? '-'
    })

    // Tags
    const customTags = computed(() => {
      return filter(tags.value, (i) => i.uiGroup === '_custom')
    })

    // Feature Flags
    const usePospal = computed(() => checkAction('admin.shopCustomModule.posPalMemberWallet'))
    const useAppointmentFeature = computed(() => checkAction('admin.appointmentReservation.page'))
    const useExternalPoint = computed(() => checkAction('admin.externalPointWallet.getExternalPoint'))
    const useMessageNotification = computed(() => checkAction('admin.member.sentLineMessage'))

    /**
     * 使用 memberTopInfo.cashback 判斷是否啟用回饋金
     * 當 cashback 為 null 時表示未啟用
     */
    const useShopRebate = computed(() => {
      return checkActionList(
        [
          'admin.member.findOneCashbackWallet',
          'admin.member.addCashbackWalletRecord',
        ],
        'or',
      )
    })

    const useShopPoint = computed(() => {
      return checkActionList(
        [
          'admin.member.findOnePointWallet',
          'admin.member.addPointWalletRecord',
        ],
        'or',
      )
    })

    /**
     * External Wallet Config from GetMemberTopInfo
     * 使用 memberTopInfo.externalWalletConfig 取代原本的 GetExternalWalletConfig API
     */
    const externalWalletConfig = computed(() => {
      return memberTopInfo.value?.externalWalletConfig || null
    })

    const externalWalletName = computed(() => {
      return get(externalWalletConfig.value, 'displayName') || t('member.memberProfile.externalWalletDefaultName')
    })

    // Show Control
    const showControl = computed(() => {
      const controls = {
        btn: {
          syncPOS: {
            pospal: showPospalSyncBtn.value,
            tkh: checkAction('admin.shopCustomModule.syncTkhspcMemberImmidately') &&
                 !get(member.value, 'UserInfo.systemCustom.tkhspcUid'),
            cyj: checkAction('admin.shopCustomModule.syncCyjPosMemberImmediately') &&
                 !get(member.value, 'UserInfo.systemCustom.cyjPosCsid'),
          },
        },
      }
      return controls
    })

    // ========================================
    // Methods
    // ========================================

    /**
     * 統一取得會員頂部資訊
     * 這是核心方法，整合了所有會員資訊的獲取
     * 使用防去重來避免短時間內重複呼叫
     */
    const fetchMemberTopInfo = async (id = memberId.value) => {
      if (!id) {
        console.warn('fetchMemberTopInfo: memberId is required')
        return null
      }

      // 如果已經有一個 fetch 請求正在進行，跳過這一次
      if (loading.fetchingTopInfo) {
        console.log('🔄 fetchMemberTopInfo skipped (already fetching)', { id })
        return null
      }

      console.log('🔄 fetchMemberTopInfo called', { id })

      loading.fetchingTopInfo = true
      try {
        const [res, err] = await GetMemberTopInfo({
          shopId: shopId.value,
          id,
        })

        if (err) {
          // 根據錯誤類型處理
          if (err.status === 403) {
            window.$message.error(t('member.memberProfile.error.noPermission'))
          } else if (err.status === 404) {
            window.$message.error(t('member.memberProfile.error.notFound'))
            router.push({ name: 'MembersManage' })
          } else {
            window.$message.error(err.message || t('member.memberProfile.error.fetchFailed'))
          }
          return null
        }

        // 處理部分欄位錯誤
        if (res.errors && res.errors.length > 0) {
          const errorFields = join(map(res.errors, e => e.key), ', ')
          console.warn(`部分欄位查詢失敗: ${errorFields}`, res.errors)
        }

        memberTopInfo.value = res

        return res
      } catch (error) {
        console.error('fetchMemberTopInfo error:', error)
        window.$message.error({
          message: error?.message || t('member.memberProfile.error.fetchFailed'),
        })
        return null
      } finally {
        loading.fetchingTopInfo = false
      }
    }

    /**
     * 刷新預約統計
     * 使用 GetMemberTopInfo 回傳的 appointmentOrderStatusCount 資料
     */
    const refreshReservationStatistic = () => {
      if (!memberTopInfo.value?.appointmentOrderStatusCount) {
        reservationStatistic.total = [0]
        reservationStatistic.absence = [0]
        return
      }

      const statusCount = memberTopInfo.value.appointmentOrderStatusCount

      // 計算 total: wait + complete + absence
      const waitCount = statusCount.wait || 0
      const completeCount = statusCount.complete || 0
      const absenceCount = statusCount.absence || 0

      reservationStatistic.total = [waitCount + completeCount + absenceCount]
      reservationStatistic.absence = [absenceCount]
    }

    /**
     * 更新會員標籤
     */
    const updateMemberTags = async (tags) => {
      try {
        await UpdateMember({
          shopId: shop.value,
          id: memberId.value,
          mTags: tags,
        })
        await fetchMemberTopInfo()
        window.$message.success(t('member.memberProfile.updateSuccess'))
      } catch (error) {
        console.error('updateMemberTags error:', error)
        window.$message.error({
          message: error?.message || t('member.memberProfile.error.updateTagFailed'),
        })
      }
    }

    /**
     * POS 同步相關
     */
    const getPospalClientConfig = async () => {
      const [res, err] = await GetPospalClientConfig({ shopId: shopId.value })
      if (err) {
        window.$message.error(err)
        return null
      }
      return res
    }

    const checkPospalMemberSync = async () => {
      if (!memberId.value) return false

      let existMember = false
      await simpleFetch(
        CheckPospalMemberSync,
        { shopId: shopId.value, memberId: memberId.value },
        (res) => {
          if (!get(res, 'result.PosPalServiceMember.customerUid')) {
            showPospalSyncBtn.value = true
            existMember = false
          } else {
            existMember = true
          }
        },
      )
      return existMember
    }

    const syncPOSMember = async () => {
      loading.syncPOSMember = true

      const callList = []

      if (showControl.value.btn.syncPOS.pospal) {
        callList.push(async () => {
          const res = await simpleFetch(SyncPospalMember, {
            shopId: shopId.value,
            memberId: memberId.value,
          })
          const resultMsg = get(res, 'msg')
          if (resultMsg !== 'Success') {
            window.$message.error(`${t('member.memberProfile.error.syncFailed')}${resultMsg}`)
            return
          }
          if (res === 'error') {
            return
          }
          window.$message.success(t('member.memberProfile.syncSuccess'))
          await fetchMemberTopInfo()
          showPospalSyncBtn.value = false
        })
      }

      if (showControl.value.btn.syncPOS.tkh) {
        callList.push(async () => {
          const [, err] = await SyncTkhspcMemberImmidately({
            shopId: shopId.value,
            memberId: memberId.value,
          })
          if (err) {
            window.$message.error(err)
          }
          await fetchMemberTopInfo()
        })
      }

      if (showControl.value.btn.syncPOS.cyj) {
        callList.push(async () => {
          const [, err] = await SyncCyjPosMemberImmediately({
            shopId: shopId.value,
            memberId: memberId.value,
          })
          if (err) {
            window.$message.error(err)
          }
          await fetchMemberTopInfo()
        })
      }

      await Promise.all(map(callList, (fn) => fn()))
        .finally(() => {
          loading.syncPOSMember = false
        })
    }

    /**
     * 發送個人訊息推播
     */
    const addBlock = () => {
      if (notifyMessageStack.value.length === 5) return
      notifyMessageStack.value.push({
        type: 'text',
        content: '',
        action: undefined,
        actionContent: undefined,
      })
    }

    const removeNotifyMessage = (index) => {
      if (notifyMessageStack.value.length === 1) return
      notifyMessageStack.value.splice(index, 1)
    }

    const resetMessage = () => {
      notifyMessageStack.value = [
        {
          type: 'text',
          content: '',
          action: undefined,
          actionContent: undefined,
        },
      ]
    }

    const sendMessage = async () => {
      const messages = map(notifyMessageStack.value, (item) => omit(item, ['Image']))
      try {
        if (onError.value) return
        if (
          notifyMessageStack.value.length === 1 &&
          notifyMessageStack.value[0].content === '' &&
          notifyMessageStack.value[0].actionContent === undefined
        ) {
          window.$message.warning(t('member.memberProfile.error.emptyMessage'))
          return
        }
        await sentLineMessage({
          shopId: shop.value,
          id: member.value.id,
          useMulticast: false,
          messages,
        })
        window.$message.success(t('member.memberProfile.sendMessageSuccess'))
        resetMessage()
      } catch (error) {
        console.error('sendMessage error:', error)
        window.$message.error(error)
      }
    }

    /**
     * Tabs 相關
     */
    const tabsClick = ({ name }) => {
      console.log('tabsClick', name)
      ls.setActiveTab(name)
    }

    const onUpdateActiveTab = (tab) => {
      console.log('onUpdateActiveTab', tab)
      activeTab.value = tab
      ls.setActiveTab(tab)
      router.replace({
        query: {
          ...route.query,
          tab,
        },
      })
    }

    /**
     * 其他工具方法
     */
    const isEmpty = (obj) => {
      return isEmptyObj(obj)
    }

    // ========================================
    // Lifecycle Hooks
    // ========================================

    fetchTagsOnMounted()
    onMounted(async () => {
      // 初始化 Tags

      // 設定初始 Tab
      const requestedTab = route.query.tab || ls.getActiveTab()

      // 獲取會員資訊（核心方法）
      const topInfo = await fetchMemberTopInfo()
      if (!topInfo) return // 如果獲取失敗，提前返回

      if (requestedTab) {
        const exist = find(memberProfileTabs, { value: requestedTab }) ||
                      find(topInfo.shopPoints || [], { key: requestedTab })
        if (exist) activeTab.value = requestedTab
      }

      // 預約統計 - 使用 topInfo 的 appointmentOrderStatusCount
      if (useAppointmentFeature.value) {
        refreshReservationStatistic()
      }

      // POS 同步檢查
      // 注意：externalWalletConfig 已從 topInfo 獲取，無需額外呼叫 getExternalWalletConfig
      if (usePospal.value && checkAction('admin.shopCustomModule.getPosPalConfig')) {
        const configData = await getPospalClientConfig()
        if (get(configData, 'enable') && !isNull(configData) && !isEmpty(configData)) {
          await checkPospalMemberSync()
        }
      }
    })

    // 監聽路由變化
    watch(memberId, async (newId, oldId) => {
      if (newId && newId !== oldId) {
        await fetchMemberTopInfo(newId)
        if (useAppointmentFeature.value) {
          refreshReservationStatistic()
        }
      }
    })

    // ========================================
    // Provide (提供給子組件)
    // ========================================
    provide('memberTopInfo', memberTopInfo)
    provide('member', member)
    provide('levelList', levelList)
    provide('rebateWalletBalance', rebateWalletBalance)
    provide('totalPaymentPriceSum', totalPaymentPriceSum)
    provide('pointWalletBalance', pointWalletBalance)
    provide('pospalWalletBalance', pospalWalletBalance)
    provide('externalPointWalletBalance', externalPointWalletBalance)
    provide('reservationStatistic', reservationStatistic)
    provide('tags', tags)
    provide('customTags', customTags)
    provide('usePospal', usePospal)
    provide('useExternalPoint', useExternalPoint)
    provide('externalWalletName', externalWalletName)

    // 提供更新方法
    provide('updateMemberTags', updateMemberTags)
    provide('fetchMemberTopInfo', fetchMemberTopInfo)

    // ========================================
    // Return (暴露給模板使用)
    // ========================================

    return {
      // State
      member,
      memberTopInfo,
      memberId,
      activeTab,

      loading,
      showPospalSyncBtn,
      onError,
      notifyMessageStack,
      reservationStatistic,

      // Computed
      shop,
      tags,
      customTags,
      levelList,
      rebateWalletBalance,
      totalPaymentPriceSum,
      pointWalletBalance,
      pospalWalletBalance,
      externalPointWalletBalance,
      showControl,

      // Feature Flags
      usePospal,
      useAppointmentFeature,
      useExternalPoint,
      useMessageNotification,
      useShopRebate,
      useShopPoint,

      // Methods
      fetchMemberTopInfo,
      refreshReservationStatistic,
      updateMemberTags,
      syncPOSMember,
      addBlock,
      removeNotifyMessage,
      resetMessage,
      sendMessage,
      tabsClick,
      isEmpty,
      onUpdateActiveTab,

      // Composables
      checkAction,
      checkActionList,
      externalWalletName,
    }
  },
})
</script>

<style scoped lang="scss">
.member-profile {
  position: relative;

  section {
    margin-bottom: 17px;
  }
}
</style>

<style lang="scss">
.member-profile .el-tabs--card > .el-tabs__header .el-tabs__item.is-top {
  margin-right: 24px;
}

.add__popover {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
}

.add__popover__checkbox {
  display: flex;
  align-items: center;
  margin: 0;

  .el-checkbox__label {
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .el-checkbox__inner {
    border: 1px solid #767676;
  }
}
</style>
